import React, {useMemo, useState} from "react";
import {InputText} from "primereact/inputtext";
import Icon from "../../../system/components/icon";
import {
    MEETING_STATUS,
    actionPriority,
    attendance,
    daysFromToday,
    doneShare,
    dueLabel,
    faDate,
    faDay,
    faDigits,
    faMonth,
    faNum,
    faShortDate,
    faWeekday,
    isActionable,
    isUnresolved,
    plainOf,
    topicKey,
} from "./meetings-labels";
import {AvatarStack, Empty, LocationText, PersonInline, Progress, StatusPill, personOf} from "./meetings-ui";

// The three views of the meetings tab (spec 5.1, 5.2, 5.8): «نیازمند اقدام», «جلسات», «تصمیم‌ها و پیگیری‌ها».

const tasksOf = (tab, decision) => decision.taskKeys.map((key) => tab.tasks.find((task) => task.key === key)).filter(Boolean);
// the tasks that carry out a decision (the rest of the project's tasks are not «meeting» commitments)
export const decisionTasks = (tab) => {
    const keys = new Set(tab.decisions.filter((decision) => decision.status !== "cancelled").flatMap((decision) => decision.taskKeys));
    return tab.tasks.filter((task) => keys.has(task.key));
};
const matches = (text, query) => !query.trim() || topicKey(text).includes(topicKey(query));

// ---------- «نیازمند اقدام» ----------
export function OverviewView({tab, open}) {
    const today = tab.today;
    const actionable = useMemo(() => tab.decisions.filter((decision) => isActionable(decision, today))
        .sort((a, b) => actionPriority(a, today) - actionPriority(b, today)), [tab.decisions, today]);
    const upcoming = useMemo(() => tab.meetings.filter((meeting) => meeting.status === "scheduled")
        .sort((a, b) => `${a.date}${a.start}`.localeCompare(`${b.date}${b.start}`)), [tab.meetings]);
    const held = tab.meetings.filter((meeting) => meeting.status === "held");
    const commitments = decisionTasks(tab);
    const lateTasks = commitments.filter((task) => task.status !== "done" && task.dueDate && daysFromToday(task.dueDate, today) < 0);
    const doneTasks = commitments.filter((task) => task.status === "done").length;
    const doneDecisions = tab.decisions.filter((decision) => decision.status === "done").length;
    const next = upcoming[0];

    return (
        <div className="flex flex-column gap-3">
            <div className="flex align-items-end justify-content-between gap-3 flex-wrap">
                <div>
                    <span className="pd-tiny text-500">نمای پروژه</span>
                    <h2 className="pd-title text-base mt-1">چه چیزی نیاز به پیگیری دارد؟</h2>
                    <p className="pd-sub">به‌جای شمردن جلسه‌ها، از تصمیم‌های باز و قدم بعدی شروع کنید.</p>
                </div>
                <span className="pd-pill tone-gray"><span className="mt-live-dot"/>{`امروز · ${faWeekday(today)} ${faShortDate(today)}`}</span>
            </div>

            <section className="pd-kpis">
                <button type="button" className="pd-card pd-kpi" onClick={() => open.view("decisions")}>
                    <div className="flex align-items-center justify-content-between"><span className="pd-label">نیازمند پیگیری</span><Icon icon="mi mi:priority_high" className="tone-red"/></div>
                    <div className="flex align-items-end gap-2 mt-2"><span className="pd-kpi-value">{faNum(actionable.length)}</span><span className="pd-tiny text-500 mb-1">تصمیم</span></div>
                </button>
                <button type="button" className="pd-card pd-kpi" onClick={() => open.view("meetings", "scheduled")}>
                    <div className="flex align-items-center justify-content-between"><span className="pd-label">جلسه در پیش</span><Icon icon="mi mi:event_upcoming" className="tone-blue"/></div>
                    <div className="flex align-items-end gap-2 mt-2"><span className="pd-kpi-value">{faNum(upcoming.length)}</span></div>
                </button>
                <button type="button" className="pd-card pd-kpi" onClick={() => open.view("decisions", "done")}>
                    <div className="flex align-items-center justify-content-between"><span className="pd-label">تصمیم اجرا شده</span><Icon icon="mi mi:flag" className="tone-green"/></div>
                    <div className="flex align-items-end gap-2 mt-2"><span className="pd-kpi-value">{faNum(doneDecisions)}</span><span className="pd-tiny text-500 mb-1">{`از ${faNum(tab.decisions.length)}`}</span></div>
                </button>
                <button type="button" className="pd-card pd-kpi" onClick={() => open.view("decisions")}>
                    <div className="flex align-items-center justify-content-between"><span className="pd-label">کارهای تصمیم‌ها</span><Icon icon="mi mi:checklist" className="tone-amber"/></div>
                    <div className="flex align-items-end gap-2 mt-2"><span className="pd-kpi-value">{`${faNum(doneShare(commitments))}٪`}</span><span className="pd-tiny text-500 mb-1">{`${faNum(doneTasks)} از ${faNum(commitments.length)} انجام شده`}</span></div>
                </button>
            </section>

            <div className="pd-grid">
                <section className="pd-card pd-card-pad pd-col-7 flex flex-column gap-2">
                    <div className="flex align-items-center justify-content-between gap-2">
                        <div>
                            <span className="pd-tiny text-500">اولویت امروز</span>
                            <h3 className="pd-title text-sm">نیازمند اقدام</h3>
                            <p className="pd-sub">{`${faNum(actionable.length)} مورد با قدم بعدی مشخص`}</p>
                        </div>
                        <button type="button" className="pd-link pd-tiny" onClick={() => open.view("decisions")}>همه‌ی پیگیری‌ها</button>
                    </div>
                    {actionable.slice(0, 4).map((decision) => <ActionRow key={decision.key} decision={decision} tab={tab} onClick={() => open.decision(decision.key)}/>)}
                    {!actionable.length && <div className="mt-quiet"><Icon icon="mi mi:done_all" className="tone-green"/>فعلاً مورد فوری برای پیگیری نیست.</div>}
                    {lateTasks.length > 0 && (
                        <div className="mt-late">
                            <Icon icon="mi mi:schedule" className="tone-red"/>
                            <span className="flex-1 text-xs"><b>{`${faNum(lateTasks.length)} کار`}</b> از موعد گذشته‌اند؛ وضعیت و مانع هرکدام را بررسی کنید.</span>
                            <button type="button" className="pd-link pd-tiny" onClick={() => open.task(lateTasks[0].key)}>دیدن اولین کار</button>
                        </div>
                    )}
                </section>

                <section className="pd-card pd-card-pad pd-col-5 flex flex-column gap-2">
                    <div>
                        <span className="pd-tiny text-500">از جلسه‌ی قبل ادامه بده</span>
                        <h3 className="pd-title text-sm">آماده‌سازی جلسه بعد</h3>
                    </div>
                    {next ? <NextMeeting meeting={next} tab={tab} open={open}/> : <Empty icon="event_upcoming" text="جلسه‌ی آینده‌ای ثبت نشده است."/>}
                    {upcoming.slice(1, 3).map((meeting) => (
                        <button key={meeting.key} type="button" className="mt-mini-row" onClick={() => open.meeting(meeting.key)}>
                            <span className="mt-mini-date"><b>{faShortDate(meeting.date)}</b><small>{faDigits(meeting.start)}</small></span>
                            <span className="flex-1 text-xs font-bold text-right">{meeting.title}</span>
                            <Icon icon="mi mi:chevron_left" className="text-400"/>
                        </button>
                    ))}
                </section>

                <section className="pd-card pd-card-pad pd-col-7 flex flex-column gap-2">
                    <div className="flex align-items-center justify-content-between gap-2">
                        <div>
                            <span className="pd-tiny text-500">اثر پیگیری</span>
                            <h3 className="pd-title text-sm">جلسه‌ها به کجا رسیده‌اند؟</h3>
                            <p className="pd-sub">کار تکمیل‌شده همیشه به‌معنی تحقق نتیجه نیست.</p>
                        </div>
                        <button type="button" className="pd-link pd-tiny" onClick={() => open.view("decisions")}>مشاهده تصمیم‌ها</button>
                    </div>
                    <div className="mt-flow">
                        <div><b>{faNum(held.length)}</b><span>جلسه برگزارشده</span><small>{`${faNum(tab.decisions.length)} تصمیم ثبت‌شده`}</small></div>
                        <Icon icon="mi mi:arrow_back" className="text-300"/>
                        <div><b>{faNum(commitments.length - doneTasks)}</b><span>تعهد باز</span><small className={lateTasks.length ? "tone-red" : ""}>{`${faNum(lateTasks.length)} کار معوق`}</small></div>
                        <Icon icon="mi mi:arrow_back" className="text-300"/>
                        <button type="button" onClick={() => open.view("decisions", "done")}><b className="tone-green">{faNum(doneTasks)}</b><span>کار تکمیل‌شده</span><small>مرور تصمیم‌ها و ثبت نتیجه</small></button>
                    </div>
                </section>

                <section className="pd-card pd-card-pad pd-col-5 flex flex-column gap-2">
                    <div className="flex align-items-center justify-content-between gap-2">
                        <div>
                            <span className="pd-tiny text-500">آخرین فعالیت</span>
                            <h3 className="pd-title text-sm">جلسه‌های اخیر</h3>
                        </div>
                        <button type="button" className="pd-link pd-tiny" onClick={() => open.view("meetings")}>همه‌ی جلسه‌ها</button>
                    </div>
                    {held.slice(0, 3).map((meeting) => (
                        <button key={meeting.key} type="button" className="mt-mini-row" onClick={() => open.meeting(meeting.key)}>
                            <span className="mt-mini-date"><b>{faShortDate(meeting.date)}</b><small>{faDigits(meeting.start)}</small></span>
                            <span className="flex-1 min-w-0 text-right">
                                <b className="text-xs block">{meeting.title}</b>
                                <span className="pd-tiny text-500">{`${faNum(meeting.decisionKeys.length)} تصمیم · ${faNum(meeting.present.length)} حاضر`}</span>
                            </span>
                            <AvatarStack tab={tab} keys={meeting.present} max={3}/>
                        </button>
                    ))}
                    {!held.length && <Empty icon="event_available" text="هنوز جلسه‌ی برگزارشده‌ای ثبت نشده است."/>}
                </section>
            </div>

            <Participation tab={tab}/>
            <div className="mt-impact">
                <Icon icon="mi mi:forum" className="tone-blue"/>
                <span className="flex-1 text-xs">اگر موضوعی چند جلسه تکرار شده اما تصمیم یا نتیجه ندارد، آن را به دستور جلسه‌ی بعد اضافه کنید.</span>
                {next && <button type="button" className="pd-link pd-tiny" onClick={() => open.meeting(next.key)}>بررسی موضوعات باز</button>}
            </div>
        </div>
    );
}

function ActionRow({decision, tab, onClick}) {
    const due = dueLabel(decision.dueDate, tab.today);
    const needsResult = decision.status === "done" && !decision.resultVerified;
    const firstTask = tasksOf(tab, decision)[0];
    const helper = needsResult ? "کارها انجام شده‌اند؛ نتیجه‌ی تصمیم را ثبت و تأیید کنید."
        : (decision.blockerStatus === "open" && decision.blocker) || (firstTask ? `کار مرتبط: ${firstTask.title}` : "برای این تصمیم، قدم بعدی را مشخص کنید.");
    const tone = needsResult ? "green" : decision.status === "blocked" || due.tone === "red" ? "red" : "amber";
    return (
        <button type="button" className={`pd-attention tone-${tone === "green" ? "amber" : tone} mt-action-row`} onClick={onClick}>
            <span className={`mt-action-icon tone-${tone}`}><Icon icon={needsResult ? "mi mi:done_all" : decision.status === "blocked" ? "mi mi:block" : "mi mi:schedule"}/></span>
            <span className="flex-1 min-w-0 text-right">
                <span className="flex align-items-center gap-2 flex-wrap"><b className="text-xs">{decision.title}</b><StatusPill kind="decision" status={decision.status}/></span>
                <span className="pd-tiny text-600 block mt-1">{helper}</span>
                <span className="mt-meta small mt-1">
                    <PersonInline tab={tab} personKey={decision.owner}/>
                    <span className={`tone-${due.tone}`}><Icon icon="mi mi:event"/>{needsResult ? "منتظر تأیید نتیجه" : due.text}</span>
                </span>
            </span>
            <Icon icon="mi mi:chevron_left" className="text-400"/>
        </button>
    );
}

function NextMeeting({meeting, tab, open}) {
    const unresolved = tab.decisions.filter(isUnresolved);
    const suggestion = unresolved.find((decision) => !meeting.decisionKeys.includes(decision.key) && !meeting.agenda.some((item) => topicKey(item) === topicKey(decision.title)));
    return (
        <div className="mt-next">
            <div className="flex align-items-center gap-2">
                <span className="mt-head-icon small"><Icon icon="mi mi:event"/></span>
                <div className="flex-1 min-w-0">
                    <span className="pd-tiny text-500">جلسه‌ی بعدی</span>
                    <b className="text-sm block">{meeting.title}</b>
                </div>
                <button type="button" className="pd-link pd-tiny" onClick={() => open.meeting(meeting.key)}>جزئیات</button>
            </div>
            <div className="mt-meta small">
                <span><Icon icon="mi mi:calendar_today"/>{faDate(meeting.date)}</span>
                <span><Icon icon="mi mi:schedule"/>{`${faDigits(meeting.start)} تا ${faDigits(meeting.end)}`}</span>
                <LocationText text={meeting.location}/>
            </div>
            <div className="flex align-items-center justify-content-between">
                <span className="text-xs font-bold">آمادگی جلسه</span>
                <span className="pd-pill tone-gray">{`${faNum(unresolved.length)} مورد حل‌نشده`}</span>
            </div>
            {suggestion ? (
                <div className="mt-suggestion">
                    <span className="flex-1 text-xs"><Icon icon="mi mi:error_outline" className="tone-amber text-sm"/>{` ${suggestion.status === "done" ? "نتیجه‌ی تأییدنشده:" : "موضوع حل‌نشده:"} `}<b>{suggestion.title}</b></span>
                    {meeting.canEdit && <button type="button" className="pd-btn small" onClick={() => open.linkDecision(meeting.key, suggestion.key)}><Icon icon="mi mi:add" className="text-sm"/>افزودن و پیوند تصمیم</button>}
                </div>
            ) : (
                <div className="mt-suggestion is-ready">
                    <span className="flex-1 text-xs"><Icon icon="mi mi:done_all" className="tone-green text-sm"/> موارد حل‌نشده در دستور جلسه هستند.</span>
                    <button type="button" className="pd-link pd-tiny" onClick={() => open.meeting(meeting.key)}>مرور دستور جلسه</button>
                </div>
            )}
        </div>
    );
}

function Participation({tab}) {
    const held = tab.meetings.filter((meeting) => meeting.status === "held");
    const keys = [...new Set([...tab.members, ...tab.meetings.flatMap((meeting) => meeting.attendees)])];
    return (
        <details className="pd-card mt-participation">
            <summary>
                <span className="mt-head-icon small"><Icon icon="mi mi:diversity_3"/></span>
                <span className="flex-1"><b className="text-xs block">مشارکت و پیگیری تعهدها</b><span className="pd-tiny text-500">برای دیدن موانع و زمینه؛ نه رتبه‌بندی افراد</span></span>
                <span className="pd-pill tone-gray">{`${faNum(held.length)} جلسه برگزارشده`}</span>
            </summary>
            <div className="mt-table">
                <div className="mt-table-row head"><span>عضو تیم</span><span>حضور</span><span>تصمیم باز · کار باز</span></div>
                {keys.map((key) => {
                    const rate = attendance(tab.meetings, key);
                    const openDecisions = tab.decisions.filter((decision) => decision.owner === key && isUnresolved(decision)).length;
                    const openTasks = decisionTasks(tab).filter((task) => task.assignee === key && task.status !== "done").length;
                    return (
                        <div key={key} className="mt-table-row">
                            <PersonInline tab={tab} personKey={key} empty="عضو سابق پروژه"/>
                            <span className="text-xs">{rate.invited ? <>{`${faNum(rate.attended)} از ${faNum(rate.invited)} `}<b>{`${faNum(rate.rate)}٪`}</b></> : <span className="text-500">بدون داده</span>}</span>
                            <span className="text-xs"><b>{faNum(openDecisions)}</b> تصمیم · <b>{faNum(openTasks)}</b> کار</span>
                        </div>
                    );
                })}
                <p className="pd-tiny text-500 m-0 p-3"><Icon icon="mi mi:help" className="text-xs"/> نرخ حضور فقط جلسه‌های برگزارشده‌ای را حساب می‌کند که فرد دعوت شده است؛ جلسه‌ی لغوشده یا برگزارنشده در مخرج نیست.</p>
            </div>
        </details>
    );
}

// ---------- «جلسات» ----------
const MEETING_FILTERS = [["all", "همه"], ["scheduled", "در پیش"], ["held", "برگزارشده"], ["cancelled", "لغوشده"], ["not_held", "برگزارنشده"]];

export function MeetingsView({tab, open, filter, setFilter}) {
    const [query, setQuery] = useState("");
    const shown = tab.meetings
        .filter((meeting) => filter === "all" || meeting.status === filter)
        .filter((meeting) => matches(`${meeting.title} ${meeting.agenda.join(" ")} ${plainOf(meeting.notes)} ${meeting.location}`, query));
    const count = (status) => (status === "all" ? tab.meetings.length : tab.meetings.filter((meeting) => meeting.status === status).length);
    return (
        <div className="flex flex-column gap-3">
            <div className="flex align-items-end justify-content-between gap-3 flex-wrap">
                <div>
                    <span className="pd-tiny text-500">آرشیو و برنامه</span>
                    <h2 className="pd-title text-base mt-1">جلسات پروژه</h2>
                    <p className="pd-sub">هر جلسه یک نقطه شروع برای تصمیم و پیگیری بعدی است.</p>
                </div>
                <div className="flex gap-2">
                    <span className="pd-pill tone-blue"><Icon icon="mi mi:event_upcoming" className="text-xs"/>{`${faNum(count("scheduled"))} جلسه در پیش`}</span>
                    <span className="pd-pill tone-green"><Icon icon="mi mi:event_available" className="text-xs"/>{`${faNum(count("held"))} برگزارشده`}</span>
                </div>
            </div>
            <div className="flex align-items-center gap-2 flex-wrap">
                <span className="p-input-icon-right flex-1 mt-search">
                    <i className="pi pi-search text-xs"/>
                    <InputText value={query} onChange={(event) => setQuery(event.target.value)} placeholder="جست‌وجوی عنوان، دستور جلسه یا متن جلسه" className="w-full text-xs"/>
                </span>
                <div className="pd-segment">
                    {MEETING_FILTERS.map(([value, label]) => (
                        <button key={value} type="button" className={filter === value ? "is-active" : ""} onClick={() => setFilter(value)}>{`${label} ${faNum(count(value))}`}</button>
                    ))}
                </div>
            </div>
            <div className="flex flex-column gap-2">
                {shown.map((meeting) => <MeetingCard key={meeting.key} meeting={meeting} tab={tab} onOpen={() => open.meeting(meeting.key)}/>)}
                {!shown.length && <Empty icon="event" text={tab.meetings.length ? "جلسه‌ای با این جست‌وجو پیدا نشد." : "هنوز جلسه‌ای برای این پروژه ثبت نشده است."}/>}
            </div>
            <p className="pd-tiny text-500 m-0"><Icon icon="mi mi:info" className="text-xs"/> «لغوشده» و «برگزارنشده» وضعیت‌های جدا هستند و در گزارش حضور با جلسه‌ی برگزارشده یکی نیستند.</p>
        </div>
    );
}

function MeetingCard({meeting, tab, onOpen}) {
    const status = MEETING_STATUS[meeting.status];
    const hasOutput = meeting.decisionKeys.length > 0;
    const people = meeting.status === "held" ? meeting.present : meeting.attendees;
    return (
        <button type="button" className={`pd-card mt-meeting-card status-${meeting.status}`} onClick={onOpen}>
            <span className="mt-date-box"><small>{faMonth(meeting.date)}</small><b>{faDay(meeting.date)}</b><small>{faDigits(meeting.start)}</small></span>
            <span className="flex-1 min-w-0 text-right flex flex-column gap-1">
                <span className="flex align-items-center gap-2 flex-wrap"><b className="text-sm">{meeting.title}</b><StatusPill kind="meeting" status={meeting.status}/></span>
                <span className="mt-meta small"><span><Icon icon="mi mi:calendar_today"/>{faDate(meeting.date)}</span><LocationText text={meeting.location}/>{meeting.owner && <PersonInline tab={tab} personKey={meeting.owner} prefix="مسئول: "/>}</span>
                {meeting.statusReason && ["not_held", "cancelled"].includes(meeting.status) && <span className="pd-tiny text-600">{`دلیل: ${meeting.statusReason}`}</span>}
                <span className="flex align-items-center gap-2">
                    <span className="pd-tiny text-600"><Icon icon="mi mi:group" className="text-xs"/>{meeting.status === "held" ? ` ${faNum(meeting.present.length)} حاضر از ${faNum(meeting.attendees.length)}` : ` ${faNum(meeting.attendees.length)} دعوت‌شده`}</span>
                    <AvatarStack tab={tab} keys={people}/>
                </span>
            </span>
            <span className={`mt-output-badge ${hasOutput ? "tone-green" : meeting.status === "held" ? "tone-amber" : "tone-gray"}`}>
                <Icon icon={`mi mi:${hasOutput ? "done_all" : meeting.status === "held" ? "pending" : status.icon}`}/>
                <span>
                    <b className="text-xs block">{hasOutput ? `${faNum(meeting.decisionKeys.length)} تصمیم` : meeting.status === "held" ? "خروجی ثبت نشده" : meeting.status === "scheduled" ? "دستور جلسه" : status.label}</b>
                    <small>{hasOutput ? "قابل پیگیری" : meeting.status === "held" ? "ثبت صورت‌جلسه و اقدام" : meeting.status === "scheduled" ? `${faNum(meeting.agenda.length)} موضوع` : "وضعیت مجزا"}</small>
                </span>
            </span>
            <Icon icon="mi mi:chevron_left" className="text-400"/>
        </button>
    );
}

// ---------- «تصمیم‌ها و پیگیری‌ها» ----------
const DECISION_FILTERS = [["all", "همه"], ["blocked", "مسدود"], ["open", "باز"], ["in_progress", "در حال اجرا"], ["done", "اجرا شده"], ["cancelled", "لغو شده"]];

export function DecisionsView({tab, open, filter, setFilter}) {
    const [query, setQuery] = useState("");
    const [lateOnly, setLateOnly] = useState(false);
    const sorted = useMemo(() => [...tab.decisions].sort((a, b) => {
        if (a.status === "blocked" && b.status !== "blocked") return -1;
        if (b.status === "blocked" && a.status !== "blocked") return 1;
        return String(a.dueDate || "9999").localeCompare(String(b.dueDate || "9999"));
    }), [tab.decisions]);
    const shown = sorted
        .filter((decision) => filter === "all" || decision.status === filter)
        .filter((decision) => !lateOnly || (decision.status !== "cancelled" && decision.status !== "done" && daysFromToday(decision.dueDate, tab.today) < 0))
        .filter((decision) => matches(`${decision.title} ${plainOf(decision.description)} ${personOf(tab, decision.owner)?.name || ""}`, query));
    const count = (status) => (status === "all" ? tab.decisions.length : tab.decisions.filter((decision) => decision.status === status).length);
    const openCount = tab.decisions.filter((decision) => !["done", "cancelled"].includes(decision.status)).length;
    const waiting = tab.decisions.filter((decision) => decision.status === "done" && !decision.resultVerified).length;
    return (
        <div className="flex flex-column gap-3">
            <div className="flex align-items-end justify-content-between gap-3 flex-wrap">
                <div>
                    <span className="pd-tiny text-500">از تصمیم تا نتیجه</span>
                    <h2 className="pd-title text-base mt-1">تصمیم‌ها و پیگیری‌ها</h2>
                    <p className="pd-sub">مسئولیت، موعد، کارهای مرتبط و نتیجه را در یک زنجیره دنبال کنید.</p>
                </div>
                <div className="flex gap-2">
                    <span className="pd-pill tone-blue">{`${faNum(openCount)} باز یا مسدود`}</span>
                    <span className="pd-pill tone-amber">{`${faNum(waiting)} نیازمند تأیید نتیجه`}</span>
                </div>
            </div>
            <div className="flex align-items-center gap-2 flex-wrap">
                <span className="p-input-icon-right flex-1 mt-search">
                    <i className="pi pi-search text-xs"/>
                    <InputText value={query} onChange={(event) => setQuery(event.target.value)} placeholder="جست‌وجوی تصمیم یا مسئول" className="w-full text-xs"/>
                </span>
                <button type="button" className={`pd-btn small ${lateOnly ? "primary" : ""}`} onClick={() => setLateOnly(!lateOnly)}><Icon icon="mi mi:filter_list" className="text-sm"/>{lateOnly ? "نمایش همه" : "فقط معوق‌ها"}</button>
            </div>
            <div className="pd-segment align-self-start flex-wrap">
                {DECISION_FILTERS.map(([value, label]) => (
                    <button key={value} type="button" className={filter === value ? "is-active" : ""} onClick={() => setFilter(value)}>{`${label} ${faNum(count(value))}`}</button>
                ))}
            </div>
            <div className="flex flex-column gap-2">
                {shown.map((decision) => <DecisionCard key={decision.key} decision={decision} tab={tab} onOpen={() => open.decision(decision.key)}/>)}
                {!shown.length && <Empty icon="flag" text={tab.decisions.length ? "موردی با این فیلتر پیدا نشد." : "هنوز تصمیمی ثبت نشده است؛ از جلسه‌ی برگزارشده «ثبت خروجی جلسه» را بزنید."}/>}
            </div>
            <div className="flex gap-3 flex-wrap pd-tiny text-500">
                <span><span className="pd-dot tone-red-bg"/> مسدود: مانع برای اقدام بعدی نیاز به پیگیری دارد</span>
                <span><span className="pd-dot tone-amber-bg"/> اجرا شده: کارها انجام شده‌اند؛ نتیجه را جداگانه ثبت/تأیید کنید</span>
            </div>
        </div>
    );
}

function DecisionCard({decision, tab, onOpen}) {
    const meeting = tab.meetings.find((item) => item.key === decision.meetingKey);
    const target = decision.target ? tab.targets.find((item) => item.type === decision.target.type && item.id === decision.target.id) : null;
    const tasks = tasksOf(tab, decision);
    const due = dueLabel(decision.dueDate, tab.today);
    const result = decision.status === "done" ? (decision.resultVerified ? ["نتیجه تأیید شده", "green"] : ["منتظر تأیید نتیجه", "amber"]) : decision.resultVerified ? ["نتیجه تأیید شده", "green"] : decision.blocker && decision.blockerStatus !== "resolved" ? ["مانع باز", "red"] : ["نتیجه هنوز ثبت نشده", "gray"];
    return (
        <button type="button" className={`pd-card mt-decision-card status-${decision.status}`} onClick={onOpen}>
            <span className="flex align-items-start gap-2 w-full">
                <span className="flex-1 min-w-0 text-right">
                    <b className="text-sm block">{decision.title}</b>
                    {decision.description && <span className="pd-tiny text-600 mt-clamp">{plainOf(decision.description)}</span>}
                </span>
                <StatusPill kind="decision" status={decision.status}/>
            </span>
            <span className="mt-meta small">
                <span><Icon icon="mi mi:event"/>{meeting?.title || "جلسه مبدأ ثبت نشده"}</span>
                <span><Icon icon="mi mi:flag"/>{target?.title || "هدف مرتبط تعیین نشده"}</span>
            </span>
            {decision.blocker && decision.blockerStatus !== "resolved" && <span className="mt-blocker"><Icon icon="mi mi:block" className="text-sm"/><span className="flex-1 text-right">{decision.blocker}</span></span>}
            <span className="mt-decision-foot">
                <PersonInline tab={tab} personKey={decision.owner}/>
                <span className={`pd-tiny tone-${due.tone}`}><Icon icon="mi mi:event" className="text-xs"/>{` ${due.text}`}</span>
                <span className="flex align-items-center gap-2 pd-tiny text-500">{`کارها · ${faNum(tasks.length)}`}<Progress value={doneShare(tasks)}/></span>
                <span className={`pd-pill tone-${result[1]}`}>{result[0]}</span>
            </span>
        </button>
    );
}
